<template>
  <section v-if="material && data" class="h-full grid grid-rows-[auto_1fr] bg-white">
    <div class="grid w-full h-full grid-rows-[auto_1fr] gap-2 overflow-hidden">
      <div class="w-full h-full px-4 py-4 overflow-auto sm:px-6">
        <div class="grid gap-4 sm:grid-cols-2 sm:grid-rows-2">
          <div class="flex flex-col items-center justify-center w-full h-full sm:items-start">
            <div class="flex items-center justify-center w-full h-full mb-2">
              <div
                @dragstart.prevent
                @drop.prevent
                class="flex items-center justify-center w-full h-full">
                <ImageUpload
                  v-if="mode == 'edit'"
                  :image="materialImage"
                  class="object-contain w-full h-auto rounded-md max-w-full lg:max-w-[300px] xl:max-w-[350px]"
                  @image-changed="onStorePhotoChange" />
                <img
                  v-else-if="materialImage"
                  :src="materialImage"
                  alt="material image"
                  class="object-contain w-full h-auto rounded-md max-w-full lg:max-w-[300px] xl:max-w-[350px]" />
                <img
                  v-else
                  src="@/assets/images/placeholder-image.webp"
                  alt="placeholder"
                  class="object-contain w-full h-auto rounded-md max-w-full lg:max-w-[300px] xl:max-w-[350px]" />
              </div>
            </div>
          </div>

          <div class="flex flex-wrap items-start w-full gap-4">
            <!-- Company -->
            <div class="w-full">
              <label class="text-sm font-medium">{{ $t('material.company') }}</label>
              <BaseSelectDropdown
                :items="dropdownCompanies"
                :selected-item="selectedCompany"
                name="companies"
                flat
                :mode="mode"
                size="sm"
                class="w-full"
                show-error-as-tooltip
                :error="
                  validations?.company.required.$invalid
                    ? validations?.company.required.$message
                    : ''
                "
                @update:selected="handleCompaniesSelection" />
            </div>

            <!-- Code -->
            <div class="w-full">
              <label class="text-sm font-medium">{{ $t('material.code') }}</label>
              <BaseInput
                :mode="mode"
                v-model="data.code"
                color="base"
                flat
                class="w-full"
                :disabled="mode == 'view'"
                show-error-as-tooltip
                :error="
                  validations?.code.required.$invalid
                    ? validations?.code.required.$message
                    : validations?.code.maxLength.$invalid
                      ? validations.code.maxLength.$message
                      : ''
                " />
            </div>

            <!-- Small Code -->
            <div class="w-full">
              <label class="text-sm font-medium">{{ $t('material.smallCode') }}</label>
              <BaseInput
                :mode="mode"
                v-model="data.smallCode"
                color="base"
                flat
                class="w-full"
                :disabled="mode == 'view'" />
            </div>

            <!-- Name -->
            <div class="w-full">
              <label class="text-sm font-medium">{{ $t('material.name') }}</label>
              <BaseInput
                :mode="mode"
                v-model="data.name"
                color="base"
                flat
                class="w-full"
                :disabled="mode == 'view'"
                show-error-as-tooltip
                :error="
                  validations?.name.required.$invalid ? validations?.name.required.$message : ''
                " />
            </div>
          </div>

          <div class="flex flex-wrap items-start w-full gap-4">
            <!-- Small Name -->
            <div class="w-full">
              <label class="text-sm font-medium">{{ $t('material.smallName') }}</label>
              <BaseInput
                :mode="mode"
                v-model="data.smallName"
                color="base"
                flat
                class="w-full"
                :disabled="mode == 'view'" />
            </div>

            <!-- Material Type -->
            <div class="w-full">
              <label class="text-sm font-medium">{{ $t('material.materialType') }}</label>
              <BaseSelectDropdown
                :items="dropdownMaterialTypes"
                :selected-item="selectedMaterialType"
                name="materialType"
                flat
                :mode="mode"
                size="sm"
                class="w-full"
                show-error-as-tooltip
                clearable
                :error="
                  validations?.materialType.required.$invalid
                    ? validations?.materialType.required.$message
                    : ''
                "
                @update:selected="handleMaterialTypeSelection" />
            </div>

            <!-- VAT Category -->
            <div class="w-full">
              <label class="text-sm font-medium">{{ $t('material.vatCategory') }}</label>
              <BaseSelectDropdown
                :items="dropdownVatCategories"
                :selected-item="selectedVatCategory"
                name="vatCategory"
                flat
                :mode="mode"
                size="sm"
                class="w-full"
                clearable
                show-error-as-tooltip
                :error="
                  validations?.vatCategory.required.$invalid
                    ? validations?.vatCategory.required.$message
                    : ''
                "
                @update:selected="handleVatCategorySelection" />
            </div>

            <!-- Is Active -->
            <div class="flex items-center justify-between w-full">
              <label class="text-sm font-medium">{{ $t('baseObject.isActive') }}</label>
              <div>
                <BaseTableCellStatus
                  v-if="mode == 'view'"
                  :item="data.isActive ? data.isActive : 0" />
                <BaseToggle
                  v-else
                  :label="$t('baseObject.isActive')"
                  name="isActive"
                  class="w-full"
                  v-model="data.isActive" />
              </div>
            </div>
          </div>

          <div class="flex flex-wrap items-start w-full gap-4">
            <!-- Is From Manager -->
            <div class="w-full">
              <label class="text-sm font-medium">{{ $t('baseObject.isFromManager') }}</label>
              <div>
                <BaseTableCellCheck :item="data.isFromManager ? data.isFromManager : false" />
              </div>
            </div>

            <!-- Last Update -->
            <div class="w-full">
              <label class="text-sm font-medium">{{ $t('baseObject.lastUpdate') }}</label>
              <div class="mt-2">
                {{ isoDateToFullDate(data.lastUpdate) || '-' }}
              </div>
            </div>

            <!-- Update User -->
            <div class="w-full">
              <label class="text-sm font-medium">{{ $t('baseObject.updateUser') }}</label>
              <div class="mt-2">
                {{ data.updateUser?.name || '-' }}
              </div>
            </div>

            <!-- Created Date -->
            <div class="w-full">
              <label class="text-sm font-medium">{{ $t('baseObject.createdDate') }}</label>
              <div class="mt-2">
                {{ isoDateToFullDate(data.createdDate) || '-' }}
              </div>
            </div>

            <!-- Create User -->
            <div class="w-full">
              <label class="text-sm font-medium">{{ $t('baseObject.createUser') }}</label>
              <div class="mt-2">
                {{ data.createUser?.name || '-' }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useLookupsStore } from '@/stores/lookups';
import { isoDateToFullDate } from '@/utils/date';
import { useI18n } from 'vue-i18n';

import BaseTableCellCheck from '../common/BaseTableCellCheck.vue';
import BaseTableCellStatus from '../common/BaseTableCellStatus.vue';
import BaseInput from '../common/BaseInput.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import BaseToggle from '../common/BaseToggle.vue';
import ImageUpload from '../common/ImageUpload.vue';

import { type Material } from '@/types/Materials';
import type { SelectDropdownItem } from '@/types/Base';
import { type Validation } from '@vuelidate/core';
import { convertBlobToBase64 } from '@/utils/image';

defineProps<{
  material: Material | undefined;
  mode: 'view' | 'edit';
  isHorizontalLayout: boolean;
  validations?: Validation;
}>();

const { t: $t } = useI18n({ useScope: 'global' });

const data = defineModel<Material>();
const materialImage = ref<string>('');

const { dropdownCompanies, dropdownMaterialTypes, dropdownVatCategories } =
  storeToRefs(useLookupsStore());

const selectedCompany = ref<SelectDropdownItem | null>(null);

function handleCompaniesSelection(item: SelectDropdownItem) {
  selectedCompany.value = item;
  if (data.value) data.value.companyId = item.value as string;
}

const selectedMaterialType = ref<SelectDropdownItem | null>(null);

function handleMaterialTypeSelection(item: SelectDropdownItem) {
  selectedMaterialType.value = item ? item : null;
  if (data.value) data.value.materialTypeId = item ? (item.value as string) : undefined;
}

const selectedVatCategory = ref<SelectDropdownItem | null>(null);

function handleVatCategorySelection(item: SelectDropdownItem) {
  selectedVatCategory.value = item ? item : null;
  if (data.value) data.value.vatCategoryId = item ? (item.value as string) : undefined;
}

function initDropdowns() {
  if (data.value) {
    const selectedValue = dropdownCompanies.value.find(
      (item) => item.value == data.value?.companyId
    );
    if (selectedValue) selectedCompany.value = selectedValue;
    else if (dropdownCompanies.value?.[0]) handleCompaniesSelection(dropdownCompanies.value?.[0]);

    if (data.value.materialType && data.value.materialTypeId) {
      const selectValue = dropdownMaterialTypes.value.find(
        (item) => item.value == data.value?.materialTypeId
      );
      if (selectValue) selectedMaterialType.value = selectValue;
    }
    if (data.value.vatCategory && data.value.vatCategoryId) {
      const selectValue = dropdownVatCategories.value.find(
        (item) => item.value == data.value?.vatCategoryId
      );
      if (selectValue) selectedVatCategory.value = selectValue;
    }
  }
}

watch(
  () => data.value,
  () => {
    initDropdowns();
  },
  { deep: true }
);

onMounted(() => {
  initDropdowns();
  if (data.value && data.value.image) {
    materialImage.value = `data:image/jpeg;base64,${data.value?.image}`;
  }
});

async function onStorePhotoChange(image: string) {
  if (!data.value) return;
  if (!image) {
    data.value.image = undefined;
    return;
  }

  if (image.startsWith('blob')) {
    convertBlobToBase64(image)
      .then((base64Image) => {
        if (data.value)
          data.value.image = base64Image.replace(/^data:image\/[a-zA-Z]+;base64,/, '');
      })
      .catch((error) => {
        console.error('Error converting blob to Base64:', error);
      });
  } else if (image.startsWith('data:image/,') && image.endsWith(';base64,')) {
    data.value.image = image.replace(/^data:image\/[a-zA-Z]+;base64,/, '');
  }
}

watch(
  () => data.value?.image,
  () => {
    if (data.value && data.value.image) {
      materialImage.value = `data:image/jpeg;base64,${data.value?.image}`;
    } else materialImage.value = '';
  },
  { deep: true }
);
</script>
